import io from 'socket.io-client'
import {SvelteMap, SvelteSet} from "svelte/reactivity";

import {init_core, docs, protoActions, Location, define_properties, isEmpty, notEmpty, noPersona, is_observed, longDiceAudio, shortDiceAudio, fetch_documentation, protoSkills} from "./core.js";
import {publicLayouts} from "./general/_General.svelte";
import {advtLayouts} from "./prepare/_Prepare.svelte";
import {heroLayouts} from "./manage/_Manage.svelte";
import {gameLayouts} from "./narrate/_Narrate.svelte";
import {playLayouts} from "./play/_Play.svelte";
import {AvailPanels} from "./App.svelte";
import {scroll_to} from "./play/Encounter.svelte"
import {on} from "svelte/events";

const modules = {public: publicLayouts, advt: advtLayouts, hero: heroLayouts, game: gameLayouts, play: playLayouts}
const allLayouts = Object.fromEntries([].concat(
	...Object.entries(modules)
		.map(([modID, layouts]) => layouts.map(layout => [`${modID}-${layout.page}`, layout])) ) )

/*** init app  ***/

document.addEventListener('DOMContentLoaded',init_client)
let loadingCore = null


let uuid
let observedIIDs
let lastMessage

/** creates UUID and tries to login the stored player */
async function init_client() {
	loadingCore = init_core()
	/// retrieve UUID from localStorage or create a new one for identification with server
	uuid = localStorage.getItem('client')
	if (!uuid) {
        try {
            uuid = self.crypto.randomUUID()   // create client ident as UUID if no ident stored
        } catch (error) {
            uuid = "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
                (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
            );
        }
        localStorage.setItem('client', uuid)}
	console.log(`CLIENT-init: starting client ${uuid}`)
	/// check if user stored and if session is still open  (retrieve user name and try to login)
	observedIIDs = JSON.parse(sessionStorage.getItem('observeds')) ?? []    // will be used later by Game.init
	const userID = localStorage.getItem('user')
	await loadingCore   // ToDo: can we start logging in before we await core data? This would require to switch views only after loading all data
	if (userID && userID !== "undefined")  // && username.slice(0,4) !== "ANON")
		try {
			await Promise.all([loadingCore, user.login(userID)])
		} catch (err) {
			console.error(`CLIENT-init: Error during login of ${userID}.`, err)
			sessionStorage.removeItem('user')
		}
	else
		await loadingCore
    /// reset observed incidents
	if (observedIIDs.length > 0) {
		view.occurreds.clear()
		observedIIDs
			.map(incidID => game.infos.get(incidID))
			.filter(incid => incid)
			.forEach(incid => view.occurreds.add(incid))
	}
	/// switch views only after everything has been loaded
	history.replaceState('public-home', null)
	// view.layout = (game.mode) ? 'playing' : (user.name) ? 'prepare' : 'home'
	if (!game.mode && view.adventure)
		view.switch_layout('advt-general')
    else if (game.demo) {
		// view.switch_layout('play-control')
		view.show_dialog('inform', {mode: 'demo'})
    } else if (game.ID)
		view.switch_layout('play')
    else
		view.switch_layout('home')
}




/*** has all data of the logged-in user, incl. all available objects (as heroes, adventures, etc.)*/
class User {
	ID = $state("")     // user logged-in when ID != null
	name = ""
	session = ""
	get isMaster() { return !game.mode || this.ID === game.master }    //ToDo: move to game.isMaster
	/* collections of available objects */
	adventures = new SvelteMap()
	deployments = new SvelteMap()
	get heroes() {return this.characters.values().filter(char => char.ZUB === 'Hero' && char.owner === this.ID).toArray()}	// PROPERTY filtering the characters
	sections = new SvelteMap()
	exhibits = new SvelteMap()
	sites = new SvelteMap()
	layers = new SvelteMap()
	obstacles = new SvelteMap()     // environment elements on layers (walls, doors, water, ...)
	environs = new SvelteMap()
	tales = new SvelteMap()
	sessions = new SvelteMap()
	// characters
	characters = new SvelteMap()    // user can access heroes, owneds, public templates (loaded lazyly) and participants
	traits = new SvelteMap()
	proficiencies = new SvelteMap()
	masteries = new SvelteMap()
	gearitems = new SvelteMap()
	possessions = new SvelteMap()
	equipments = new SvelteMap()
	games = new SvelteMap()         // all running games
	fellowships = new SvelteMap()
	/// private fields
	#adventSurvey = false


	constructor () {}

	/** creates a new user and sets the exported user variable */
	async login(userID, password) {
		if (!userID)  // no name no login
			return
		let response
        try {
            console.log(`USER-login: logging-in user ${userID} on client ${uuid.split("-")[0]}`);
			response = await fetch_data(`/login`, {client: uuid, user: userID, pass: password})
            localStorage.setItem('user', response.ID)
            /// initialize user and game data
            this.init(response)
            if (response.game)
				game.init(response.game)
            if (response.current)
				view.init(response.current)
            if (!socket)  connect_socket()
	    } catch (err) {
            // ToDo: user.logout()
            console.error(`COMM-login: Error during login of ${userID}.`, response?.error ?? err)
	        response = {}
        }
        return response
	}

	async logout() {
        /// clear all data
		this.ID = null
		this.name = ""
		this.session = ""
        sessionStorage.removeItem('session')
        localStorage.removeItem('user')
		/// send logout to server (will cause reload of client)
        await fetch_data('/logout')
		/// reload page to reset all data
		location.reload()
	}


    /** distributes received data for login and tutorial **/
	init (data) {
		this.ID = data.ID
		this.name = data.name
	    this.session = data.session
        /// store session in local storage
        sessionStorage.setItem('session', data.session)
        if (!socket)
            connect_socket()
        // console.log(`• COMM - User object created: `, this)
	}

	/** processes array of update objects
	 * @param {string} collect  name the collection into which the updates belong
	 * @param {Array[Object]} updates   list of full or partial objects, contain .DELETE if object is to be deleted
	 */
	update(collect, updates) {
		const collection = this[collect]
		if (!(collection instanceof SvelteMap))  return
		for (const updData of updates) {
			/// create properties for characters
			if (updData.TYPE.slice(0,9) === 'Character' && !updData.LP)
				define_properties(updData)
			update_mapCollection(collection, updData)
		}
	}


	/** fetches snippets of all available adventures for this user **/
	async survey_adventures() {
		await fetch_data(`/adventures`) //ToDo: make sure that snippets are not overwriting full adventure data
		this.#adventSurvey = true
	}

	async load_adventure(advtID) {
        if (!this.adventures.has(advtID))
            return false
		try {
			await fetch_data(`/adventure/${advtID}`)
			view.adventure = this.adventures.get(advtID)
			view.tale = user.tales.get(advtID)
		} catch (err) {
			console.error(err)
            this.unload_adventure()
		}
	}

	unload_adventure() {
		/// clear all selections
        view.adventure = null
		view.section = null
		view.site = null
		view.character = null
		fetch_data('adventure/_')    // set adventure in serve to None
	}

	async load_site(siteID) {
        if (!this.sites.has(siteID))
            return false
		try {
			await fetch_data(`/site/${siteID}`)
			view.sites = this.sites.get(siteID)
		} catch (err) {
			console.error(err)
		}

	}

	select_map(mapID) {
		this.currMapID = mapID
		console.log(`Map ${mapID} has been selected.`)
	}


	/*** join game ***/

	async join_game(gameID) {
        console.log(`• CLIENT-join: Player ${this.name} wants to joined the game ${gameID}:`)
		const data = await fetch_data('join', {player: this.ID, game: gameID})
		if (data.game)
			game.init(data.game)
	}

	/**
	 * loads and starts tutorial
	 * @returns {Promise<void>}
	 */
	async commence_demo() {
		// await this.login('Anonymous')
		console.log(`CLIENT: Demo is starting`)
		const demodata = await fetch_data("/demo", {client: uuid})
		user.init(demodata)
		game.init(demodata.game)
		/// set persona
		view.select_persona(game.playables[0])
		view.switch_layout('play-present')
		view.show_dialog('inform', {mode: 'demo'})
	}

	// get isMaster() { return this.name === this.game.master }
}

export const user = new User()      // instance which contains the data for logged-in user
globalThis.user = user

const specificURLs = ['Variant']

export async function change_attribute({target: {type, id, value, checked}}) {
	let attrib, newval
	if (type === 'checkbox') {
		attrib = 'checked'
		newval = checked
	} else {
		attrib = id      // (typeof this === 'string') ? this : id
		if (!attrib)  return
		newval = (value === "") ? null : (isNaN(value)) ? value : +value
	}
	// const attrib = id      // (typeof this === 'string') ? this : id
	// if (!attrib)  return
	// const newval = (value === "") ? null : (isNaN(value)) ? value : +value
	const url = (specificURLs.includes(this.ZUB)) ? this.ZUB : this.TYPE  // some ZUBs should have specific urls (e.g. Variant)
	const data = Object.assign(
		Object.fromEntries(Object.entries(this).filter(([key,val]) => key === key.toUpperCase() && key !== 'TYPE')),
		{[attrib]: newval})
	if (this.ID) {
		console.log(`# ${url.toUpperCase()}-PATCH: In ${this.TYPE} '${this.ID}' change [${attrib}] to ${newval}.`, $state.snapshot(this))
		await send_data(`/${url.toLowerCase()}/${this.ID}`, data, 'PATCH')
	} else {    // only necessary for new proficiencies and new traits
		console.log(`# ${url.toUpperCase()}-POST: Create new ${this.TYPE} '${this.name}' for ${this.CHAR} with value ${newval}.`, $state.snapshot(this), data)
		await send_data(`/${url.toLowerCase()}/_`, data, 'POST')
	}
}

/*** contains all data of a running game session (fighters, activities, incidents timeline, etc.) */
class Game {
	ID = $state("")     // use game.ID to check if game is running
	adventure = ""
	tale = ""
	demo = null
	/// master and players
	master = null
	players = new SvelteMap()
	// array of persona IDs this user is allowed to play
	playables = $derived( (user.isMaster) ? Array.from(this.personas.values()).map(pers => pers.ID) : this.players.get(user.ID).avails)  // list of persona IDs this user is allowed to play
	//Old: playables = $derived( (user.isMaster) ? this.personas.values().map(pers => pers.ID).toArray() : this.players.get(user.name).avails)  // list of persona IDs this user is allowed to play
	heroes = []
	appearArea = {}
	// unused attributes of Game:  adventure, assigneds, characters, current
	/** state of the game */
	section = $state(null)      // ID of section currently played (null = no section started)
	time = $state(0)            // Current time in an encounter
	present = $state(null)       // current presented exhibit ID
	/** encounter states */
	mode    = $state(null)      // Literal['story', 'recon', 'combat', 'stop'], null = currently no play started
	main    = $state("")      // ID of main map (first shown)
	marker = $state(null)       // position of shared marker
	halted  = $state(true)
	occupied = $state(true)
	get orders_possible() {return (user.isMaster || ['story', 'scene', 'journey', 'recon', 'combat'].includes(this.mode))}
	get inEncounter() {return ['stop', 'script', 'recon', 'combat', 'contin', 'placing'].includes(this.mode)}
	/** collections */
	personas = new SvelteMap()
	activities = new SvelteMap()
	infos   = new SvelteMap()     // (aka timeline) array of incidents in the order of appearance
	effects = new SvelteMap()
	maps = new SvelteMap()
	exhibits = $state([])       // all exhibits available to players
	obtainables = $state([])


	constructor() { }

	async init(gamedata) {
        console.log(`  – initialising game ${gamedata.ID} with `, $state.snapshot(gamedata));
        // ({ ID: this.ID, current: this.section, mode: this.mode, master: this.master, halted: this.halted, readyToProceed: this.readyToProceed} = data);
		for (const attrib in gamedata) {
			if (this[attrib] instanceof SvelteMap)// (collect in this)
				this.update_collection(attrib, gamedata[attrib])
			else if (attrib in this)
				this[attrib] = gamedata[attrib]
		}
		/// set adventure if user is master
		if (user.isMaster)
			view.adventure = user.adventures.get(gamedata.adventure)
        /// determine current role
        let role = localStorage.getItem('role')
        if (!(this.playables.includes(role)))   // no role stored or role is not playable this time
            role = this.playables[0]
        if (role)
            view.select_persona(role)
		/// cover exhibit
		if (!user.isMaster && user.exhibits.has(gamedata.cover) && !sessionStorage.getItem('coverSeen')) {
			view.exhibit = Object.assign({}, user.exhibits.get(gamedata.cover), {title: gamedata.name, caption: ""})
			sessionStorage.setItem('coverSeen', true)
		}
		/// set map and view
        if (this.inEncounter) {
            let mapID
	        const possibleMapIDs = [sessionStorage.getItem('map'), view.persona?.position?.map, this.maps.get(this.main)?.ID, null]
	        for (mapID of possibleMapIDs) {
                if (this.maps.has(mapID)) {
			        view.switch_map(mapID)
		            view.perspective = view.persona?.ID
		            view.superimposed = false
			        break
		        }
	        }
			Location.basemap = gamedata.main
        }
		view.occurreds.clear()
		view.switch_layout('play')
	}

	/** update game data (not the collections) **/
	update(data) {
		// clear all game data if game terminated (=deleted flag)
		if ('DELETED' in data) {
			this.terminate()
			return
		}
		/// update specific attributes
		for (const attrib in data) {
			if (this[attrib] instanceof SvelteMap) {
				for (const updata of data[attrib])
					update_mapCollection(this[attrib], updata)
			} else if (attrib in this && attrib.toUpperCase() !== attrib)
				this[attrib] = data[attrib]
		}
        /// set map and view
        if (this.inEncounter) {
            let mapID
	        const possibleMapIDs = [sessionStorage.getItem('map'), view.persona?.position?.map, this.maps.get(this.main)?.ID, null]
	        for (mapID of possibleMapIDs) {
                if (this.maps.has(mapID)) {
	                if (mapID !== view.map?.ID) {
				        view.switch_map(mapID)
			            view.perspective = view.persona.ID
			            view.superimposed = false
					}
			        break   // break going through the possible maps on the first map that is in the current encounter
		        }
	        }
        }
		if ('present' in data)
			view.exhibit = user.exhibits.get(data.present)
	}

	/** processes array of update objects
	 * @param {string} collect  name the collection into which the updates belong
	 * @param {Array[Object]} updates   list of full or partial objects, contain .DELETE if object is to be deleted
	 */
	update_collection(collect, updates) {
		/// only update map collections
        if (!(this[collect] instanceof SvelteMap))  return
        /// update corresponding data in collections
		for (const updData of updates) {
			// convert all positions
			if (updData.TYPE === 'Persona' && updData.position)
				updData['position'] = new Location(updData.position)
			if ((updData.ZUB === 'Movement' || updData.ZUB === 'Charge') && updData.aim) {
				if (Array.isArray(updData.aim))
					updData['aim'] = new Location(updData.aim)
				updData['route'] = updData['route'].map(waypoint => (Array.isArray(waypoint) ? new Location(waypoint) : waypoint))
			}
			update_mapCollection(this[collect], updData)
		}
		/// collect all incidents that are observed by this player
		// if (collect === 'infos') {
		// 	for (const incid of updates) {
		// 		const incident = this.incidents.get(incid.ID)
		// 		// non-observable incidents are
		// 		if ('MUTED' in incident.flags || typeof incident.superior !== 'undefined' ||    // either muted incidents or subordinated incidents
		// 			('PRIVATE' in incident.flags && (!user.isMaster || game.playables.includes(incident.subject))))     // private incidents from non-own fighters or
		// 			continue
		// 		// create set of observable fighters (master observes all [since all are playable])
		// 		const observables = new Set(game.playables)
		// 		if (!user.isMaster) {
		// 			// add all fellows
		// 			game.personas.values().filter(pers => pers.affil === 'fellow')
		// 								  .forEach(pers => observables.add(pers.ID))
		// 			// add all fighters detected by any of the playables
		// 			game.playables
		// 				.map(persID => game.personas.get(persID))
		// 				// .forEach(pers => (pers.detecteds.length > 0) ? pers.detecteds.foreach(other => observables.add(other)) : null)
		// 				.forEach(pers => pers.detecteds.forEach(other => observables.add(other)))
		// 		}
		// 		if (incident.subject === null || observables.has(incident.subject) || observables.has(incident.target)) {
		// 			if (incident.time === game.time || incident.status === 'querying')
		// 				view.imminents.add(incident)
		// 			else if (incident.status === 'ongoing') {
		// 				view.imminents.delete(incident)
		// 				view.ongoings.add(incident)
		// 			} else if (incident.status === 'occurred') {
		// 				view.imminents.delete(incident)
		// 				view.ongoings.delete(incident)
		// 				view.occurreds.add(incident)
		// 			}
		// 			console.log(`GAME-update_collection: incident ${incident.ID} is observed by this client`)
		// 		}
		// 	}
		// 	// write IDs into session storage
		// 	const observedIIDs = view.occurreds.values().map(incid => incid.ID).toArray()
		// 	sessionStorage.setItem('observeds', JSON.stringify(observedIIDs))
		// }
	}

	async commence(gameID) {
		/// set adventure and game by setting ID, data will come via updates
		console.log(`• GAME-commence: user (${sessionStorage.getItem('session')}) is starting game ${gameID}.`)
        const response = await fetch_data('/commence', {game: gameID})
		view.adventure = user.adventures.get(gameID)
		view.tale = user.tales.get(gameID)
        this.init(response.game)
        return response
	}

	/** terminate game by sending a message to the server and then reloads the page */
	terminate() {
		if (user.isMaster)
			send_request('control', {command: 'TERMINATE'}, "Terminate game")
		this.ID = null
		this.mode = null
		view.map = view.exhibit = null
		view.timelineShown = false
		view.upcomings.clear()
		view.occurreds.clear()
		view.switch_layout('home')
	}

	async switch(sectID) {
		/** start section by sending a message via sockets to server */
		console.log(`GAME - switching section to ${sectID}`, view.section)
		if (!game.mode) {
	        const gameData = await fetch_data('/commence', {game: view.adventure.ID, section: sectID})
	        console.log(`GAME-start: This client (${sessionStorage.getItem('session')}) has started section ${sectID}: `, gameData)
	        reset_session(gameData)
	        reset_characters()
		}
		else
	        send_request('control', {command: 'SWITCH', section: sectID})
		// select corresponding section
		const section = user.sections.get(sectID)
		if (!view.section && section)  view.select_section(section)
		view.switch_layout('play-encounter')
	}

	close_scene(save = false) {
		send_request('control', {command: 'SWITCH', section: null, save: save})
	}


	enter_encounter() {
		send_request('control', {command: 'ENTER'})
	}

	leave_encounter() {
		send_request('control', {command: 'LEAVE'})
	}


	/** start a activity with given action */
	place_order(order) {
		const actor$ = game.personas.get(order?.actor) ?? view.persona
		if (!actor$)  console.error(`GAME: Activity ${order.action} cannot be started for ${actor$.ID}`, order)
		/// inserting or extendig waypoint in path
        if (['INSERT', 'EXTEND'].includes(order.action)) {
			order['actID'] = actor$.activity
	        send_request('alter', order)
	        return
		} else if (order.action === 'REMOVE') {
			send_request('instruct', {typ: 'REMOVE', subject: order.actor, target: order.mark})
	        return
        } else
			order['actor'] = actor$.ID
		/// check if activity already running
		const action$ = protoActions[order.action]
		const currentAct$ = game.activities.get(actor$.activity)
		if (currentAct$ && action$ && !action$.flags.CONCURRENT) {
	        if ((currentAct$.TYPE === "Movement" || currentAct$.TYPE === "Charge") && ['EXTEND', 'INSERT'].includes(order.action))
	            send_request('alter', {action: order.action, actID: actor$.activity, attrib: order.action.toLowerCase(), value: order.aim},
		                     `extend path of fighter ${actor$.ID}`)
	        else
	            send_request('prepare', order, `prepare next action for fighter ${actor$.ID}`)
	        return
		} else if (order.action === 'Transfer') {
			send_request('instruct', {typ: order.action.toUpperCase(), subject: actor$.ID, target: order.aim})
	        order['target'] = actor$.ID
	        order['mark'] = order.aim
	        delete order.aim
			return
		}
		/// add standard risk level for action
		const actionType = protoActions[order.action]?.TYPE ?? 'Action'
		if (!order.risk && actionType !== 'Action')
	        order['risk'] = (actionType === 'Skill') ? 2 : 1
		/// check if current map has to be added for Transfer
		// ToDo: move from ActionMenu if $currentView.map can be accessed here
		/// play sound and send request
		console.log(`GAME: Start activity ${order.action} for ${actor$.ID} with `, order)
		if (order.risk)
	        if (order.risk === 1)
	            longDiceAudio.play()
	        else if (order.risk > 1)
	            shortDiceAudio.play()
		send_request('order', order, `start activity ${order.action} for ${actor$.ID} with `)
	}

	revoke_order(actor, actID) {
		if (!actor || !actID)  return
		send_request('alter', {action: "REVOKE", actID: actID}, "Revoke order")
	}

	stop_activity(actor, actID) {
		if (!actor || !actID)  return
		let data = {action: 'ABORT', actID: actID, startprep: false}
		send_request('alter', data, "Cancel activity")
	}

	/** start a maneuver with the selected skill */
	start_maneuver(skill, risk, descript = null) {
		if (!skill)  return
		let data = {actor: view.persona.ID, action: skill, risk: risk ?? 3}
		if (descript)  data['descript'] = descript
		sound_feedback('shortDice')
		shortDiceAudio.play()
		send_request('order', data, `start maneuver/performance ${skill}`)
	}
}

export const game = new Game()   // instance which contains the data for current game session (not in preparation)
globalThis.game = game

export function create_current(data) {
	// curr = new Current(data)
	if (!socket)
        connect_socket()
}


class View {
	/// general screen layout
	mode = $derived((game.ID) ? 'playing' : (view.adventure) ? 'preparing' : (user.ID) ? 'authent' : 'public')     // Literal['public', 'authent', 'preparing', 'managing', 'playing']
	layout = $state(null)       // layout of screen: defines what is shown on main and side panel
	dialog = $state(null)       // has object on current dialog {type: str, props: {}}
	exhibit = $state(null)
	panelProps = $state(null)
	timelineShown = $state(false)
	/// selected elements
	fellowship = $state(null)   // used in narrate module
	adventure = $state(null)    // is set when preparing or playing an adventure  [MASTER-ONLY]
	section = $state(null)
	site = $state(null)
	tale = $state(null)
	character = $state(null)    // selected character during prepare or character data of persona
	persona = $state(null)      // persona (role) played
	/// encounter map states
	map = $state(null)
	dragParams = $state(null)
	option = $state(null)       // viewed potential option
	/// map view in play screen
	upcomings = new SvelteMap()     // upcoming incidents mapping (allows to delete by ID)
	occurreds = new SvelteSet()     // all incidents that this player can observe
	mapElement = null                   // HTML element of map
	mouse = $state({col: 0, row: 0, map: "", obj: ""})
	perspective = $state(null)
	superimposed = $state(false)
	selected = $state(null)
	path = $state(null)         // movement
	/// hints and documentation
	explanation = $state(null)
	help = $state(null)
	_handlerRemove = null

	init(current) {
		if (current.adventure)
			this.adventure = user.adventures.get(current.adventure)
		if (current.tale)
			this.tale = user.tales.get(current.tale)
		if (current.section)
			this.section = user.sections.get(current.section)
	}

	/** determine which incidents are observed by this player */
	update_timeline(incidents) {
		for (const incid of incidents) {
			const incident = game.infos.get(incid.ID)   // make sure that full incident data is accessed
			if (!incident) {
				if (incid.DELETED)
					view.upcomings.delete(incid.ID)
				else
					console.error(`Info ${incid.ID} is not in available incidents!`, incid)
				continue
			}
			// Old DEBUG: check that incident has flags
			if (!incident.flags) {
				console.error(`CLIENT: incident ${incid.ID} has no flags attribute`, incident)
				incident.flags = {}
			}
			// non-observable incidents are
			if ('MUTED' in incident.flags ||    // either muted incidents or
				typeof incident.superior === 'number' ||    // subordinated incidents (which is shown under the superior insight
				('PRIVATE' in incident.flags && !(user.isMaster || game.playables.includes(incident.subject))))     // private incidents from non-own fighters or
				continue
			// keep track of upcoming incidents
			if (['upcoming', 'querying', 'ongoing'].includes(incident.status)) {
				view.upcomings.set(incident.ID, incident)
				continue
			} else
				view.upcomings.delete(incident.ID)
			if (user.isMaster || incident.subject === null || is_observed(incident, game.playables)) {
				// console.log(`GAME-update_timeline: incident ${incident.ID} is observed by this client`)
				if (incident.status === 'occurred')
					this.occurreds.add(incident)
			}
		}
		// write IDs into session storage
		const observedIIDs = Array.from(this.occurreds.values()).map(incid => incid.ID)  // .toArray()
		sessionStorage.setItem('observeds', JSON.stringify(observedIIDs))
	}

	switch_layout(layoutID, props = null) {
		/// master user in play-mode will switch first only panel
		if (user.isMaster && game.ID && allLayouts[layoutID]?.panel && allLayouts[layoutID].panel !== this.layout?.panel)
			layoutID = `+${allLayouts[layoutID].panel}`

		if (layoutID.charAt(0) === '+') {
			if (!this.layout)  return
			// switch only panel
			const panelID = layoutID.slice(1)
			if (panelID in AvailPanels)
				this.layout.Master = AvailPanels[panelID]
			this.layout.panel = panelID
			this.panelProps = props
			return
		} else if (!layoutID.includes("-")) {
			if (layoutID === 'play')
				layoutID =  (game.inEncounter) ? 'play-encounter' :
							(user.isMaster) ? 'play-overview' : 'play-present'
			else if (layoutID in modules)
				layoutID += "-" + modules[layoutID][0].page
			else
				layoutID = Object.entries(allLayouts).find(([ID, layout]) => layout.page === layoutID)[0]
		}
		const newLayout$ = allLayouts[layoutID]
		if (!newLayout$) {
			console.warn(`  – VIEW-switch_layout: layout ${layoutID} not found in allLayouts`, allLayouts)
			return;
		}
        console.log(`  – VIEW-layout: switching layout to ${layoutID}`, newLayout$)
        /// catch invalid states for the given layout
        if (layoutID === 'section' && !view.section) {
            alert("Du musst zuerst in der Abenteuerübersicht einen Abschnitt auswählen!")
            return
        }
        /// back-functionality: push current layout in stack
        if (history.state !== layoutID)
            history.pushState(layoutID, null) // ToDo: , `/${newLayout$.module}/${newLayout$.page}`)
        this.layout = newLayout$
		if (layoutID === 'manage' || layoutID === 'play-overview') {
			view.timelineShown = false
		}
	}


	retrace_layout(layoutID) {
		const oldLayout = allLayouts[layoutID]
		if (!oldLayout)  return
		if (oldLayout.page === 'home')
			user.unload_adventure()
		this.layout = oldLayout
	}

	show_dialog(type, data) {
		console.log(`CLIENT: show dialog '${type}' with props {${Object.keys(data).join(", ")}}`)
		this.dialog = {type: type, props: data}
	}

	close_dialog(keep = false) {
		/// set all possible view attributes of dialogs to null
		// console.log(`CLIENT: close dialog `, $state.snapshot(this.dialog))
		if (!keep)  this.option = null      // keep option dialog
		this.dialog = null
	}

	async turnon_help() {
		console.log(`VIEW - turning help on`, this)
		// load info documents
		if (docs.length === 0)
			await fetch_documentation()
		this.show_help(`${view.layout.module}-${view.layout.page}`)
	/**
		// create anchors automatically
		const refElements = []
		for (let descendent of document.body.getElementsByTagName("*")) {
			const topic = docs.find(entry => entry.topic === descendent.innerText || entry.title === descendent.innerText)?.topic
			let inInfo = false
			let elem = descendent
			while (elem) {
			    if (elem.id === 'side-info')  inInfo = true
			    elem = elem.parentNode;
            }
			if (topic && ['H1', 'H2', 'H3', 'SPAN', 'LABEL'].includes(descendent.tagName) && !inInfo)
				refElements.push(descendent)
		}
		for (let elem of refElements)
			elem.innerHTML = `<a>${elem.innerText}</a>`
		// change anchor style rule to show/hide underlines
		for (let rule of document.styleSheets[1].cssRules)
			if (rule.selectorText === "a")
				rule.style['text-decoration-line'] = (this.help) ? "underline" : "none"
	**/
		// attach/remove the mouseover event handler
		if (this._handlerRemove) {
			this._handlerRemove()
			this._handlerRemove = null
		} else if (this.help)
			this._handlerRemove = on(document.body, 'click', view.show_help)
	}

	turnoff_help() {
		console.log(`VIEW - turning help off`, this)
		if (this._handlerRemove) {
			this._handlerRemove()
			this._handlerRemove = null
		}
		this.help = null
	}

	show_help(href) {
		console.log(`HELP - href == ${href} with view.layout`, view.layout)
		if (href?.target?.id === 'help-close') {
			view.help = null
			return
		}
		let searchTopic = href, helpEntry
		if (href instanceof Event) {
			// topic of the clicked element: own data-help, own id, label target, closest data-help, first word of text
			const target = href.target
			searchTopic =
				target.dataset?.help ??
				((target.id !== "") ? target.id : null) ??
				target.getAttribute?.('for') ??
				target.closest?.('[data-help]')?.dataset.help ??
				target.innerText?.split("\n")[0].split(" ")[0]
		}
		if (typeof searchTopic === 'string') {
			const parts = searchTopic.split("-")
			console.log(`HELP - parts`, parts)
			// const helpEntries = docs.filter(entry => (entry.chapter === parts[0] || !parts[0])
			// 	&& (entry.section === parts[1] || !parts[1]) && (entry.topic === parts[2] || !parts[2]))
			// helpEntry = (helpEntries.length > 0) ? helpEntries[0] : null
			helpEntry =
				(parts.length === 3) ? docs.find(entry => entry.chapter === parts[0] && entry.section === parts[1] && entry.topic === parts[2]) :
				(parts.length === 2) ? docs.find(entry => entry.chapter === parts[0] && entry.section === parts[1]) :
				(parts.length === 1) ? docs.find(entry => entry.chapter === view.layout.module && entry.section === view.layout.page && entry.topic === parts[0]) ??
					docs.find(entry => entry.chapter === view.layout.module && entry.section === parts[0]) : null
			if (!helpEntry)		// topic documented on another page of the same module (e.g. shared panels and dialogs)
				helpEntry = docs.find(entry => entry.chapter === view.layout.module && entry.topic === searchTopic)
			if (!helpEntry)
				helpEntry = docs.find(entry => entry.topic === searchTopic)
			if (!helpEntry)
				helpEntry = docs.find(entry => entry.title.toLowerCase() === searchTopic.toLowerCase())
		} else
			helpEntry = href
		if (!helpEntry)
			helpEntry = {chapter: view.layout.module, section: view.layout.page, topic: searchTopic, title: searchTopic}
			// helpEntry = docs.find(entry => entry.chapter === view.layout.module && entry.section === view.layout.page) ??
			// 			docs.find(entry => entry.chapter === 'intro' && entry.topic === 'help')
		console.log(`VIEW - show help on ${href} -> ${searchTopic}`, helpEntry)
		view.help = helpEntry           // use global view object instead of this since method might be event handler
		view.explanation = helpEntry
		// history.pushState("help", {})
		}

	/***** selection of elements *****/

	async select_fellowship(fshpID) {
		try {
			await fetch_data(`/fellowship/${fshpID}`, {})
			this.fellowship = user.fellowships.get(fshpID)
		} catch (err) {
			console.error(err)
		}
	}

	async select_character(charID) {
        // if (game.mode && game.playables.includes(charID) && !this.isMaster)	// players are not allowed to select outside of their playables
        //     return
        /// fetch character from if not present or only extract present
		try {
			await fetch_data(`/character/${charID}`, {})
			const selCharacter = user.characters.get(charID)
			if (!game.mode || (selCharacter?.owner === user.ID || game.playables.includes(charID) || user.isMaster))    // players are not allowed to select outside of their playables
				view.character = user.characters.get(charID) ?? noPersona
		} catch (e) {
			console.error(`Character ${charID} is not in available characters!`, e)
		}
	}

	select_persona(persID) {
		const persona = (typeof persID === 'string') ? game.personas.get(persID) : persID
		if (!persona) {
			console.warn(`Persona ${persID} is not in available personas!`)
			this.persona = null
			this.perspective = null
			this.character = null
			return
		}
		if (user.isMaster)
			this.switch_layout('+control')
		/// if the current user is not allowed to play this persona then just center the persona
        if (!(user.isMaster || game.playables.includes(persID)) || persID === view.persona?.ID) {
	        scroll_to(persona.position, true)
	        return
        }
		console.log(`• Persona ${persID} has been selected: `,$state.snapshot(persona))
		this.persona = persona
		this.perspective = persID
		/// switch tab to action
		this.character = user.characters.get(persona.character)
		localStorage.setItem('role', persID)
		scroll_to(persona.position)
	}

	select_section (sectID) {
		const section = (typeof sectID === 'string') ? user.sections.get(sectID) ?? user.sections.get(view.adventure.sections[sectID]) ?? null : sectID
		if (!section) {
			view.section = null
			// Old: game.personas = {}
			throw new Error(`Section ${sectID} does not exist in selected adventure ${view.adventure?.ID}`)
		}
		view.section = section
		// Old: populate fighters with deployments (also for adventure preparation)
		// for (const deploy in view.section.appears)
		// 	game.personas.set(deploy.fighter, deploy)
	}


	async select_site(siteID) {
		const site = (typeof siteID === 'string') ? user.sites.get(siteID) : siteID
        if (!site) {
	        console.error(`Site ${siteID} is not in available sites !`)
	        return
        } else if (site.TYPE.includes("Snippet"))
			await fetch_data(`/site/${siteID}`, {})
		this.site = site
		// switch map to main layer of this site
		const mainLayer = user.layers.get(`${site?.ID}_${site?.main}`)
		console.log(`SELECT_SITE - mainLayer (${site?.ID}_${site?.main})`, $state.snapshot(mainLayer))
		const virtualMap = Object.assign({}, mainLayer, {ID: mainLayer.LAYER, entities: [], obstacles: [], affects: []})
		this.switch_map(virtualMap)
		console.log(`VIEW - site ${site.ID} selected with main map ${virtualMap.ID}:`, $state.snapshot(site))
	}


	switch_map(mapID) {
		const map = (typeof mapID === 'string') ? game.maps.get(mapID) ?? game.maps.get(view.adventure.sites[mapID]) ?? null : mapID
		// console.log(`• Switching map to ${map?.ID ?? mapID} with `, $state.snapshot(map))
		if (!map)  return
		view.map = map
		if (view.persona)
			scroll_to(view.persona)
		sessionStorage.setItem('map', map.ID)
	}

	incidents = $derived.by(() => {     // incidents visible to this player
        const is_shown = (info) => {
			if (!info.flags)  console.log(`TIMELINE - no flag in `, $state.snapshot(info))
            const visible = !('MUTED' in info.flags)
            const targets = (info.target) ? [info.target] : []
            for (let eff in info.effects) if (eff.affected) targets.push(eff.affected)
            const forPlayer = !('PRIVATE' in info.flags) || user.isMaster || game.playables.includes(view.persona.ID) || targets.includes(view.persona.ID)
            return (visible && forPlayer)
        }
        return Array.from(game.infos.values()).filter(is_shown)
	})

	isVisible(entity) {
		return this.map && entity.position && entity.position.map === this.map.ID && entity.operative
	}
}

export const view = new View()
globalThis.view = view


/*** HTTP communication with server ***/

const htmlOptions = { // Default options are marked with *
	mode: 'same-origin',	// no-cors, *cors, same-origin
	cache: 'no-cache',      // *default, no-cache, reload, force-cache, only-if-cached
}


/** fetching data from server using fetch API **/
export async function fetch_data(url = "", data = {}) {
	if (notEmpty(data))  url += `?${new URLSearchParams(data)}`
	const options = Object.assign({method: 'GET'}, htmlOptions)
	const respPromise = await fetch(url, options)
	if (respPromise.status >= 400) {
		const errorText = await respPromise.text()
		console.error(`COMM-fetch: ${respPromise.status}-Error "${errorText}"`, {url, data})
		throw Error(errorText)
	}
	const response = await respPromise.json(); // parses JSON response into native JavaScript objects
	/// automatic updating of data
	url = url.split("?")[0]
	if (url !== '/documentation')
		process_updates(response, `${url}-fetch`)
	return response
}


/** sending data to server using fetch API **/
export async function send_data(url = "", data = {}, method = 'POST', file = null) {
	let options = {method: method}  //, ...htmlOptions}
	if (file) {
        const formData = new FormData()
        formData.append('file', file)
		formData.append('data', JSON.stringify(data))
        Object.assign(options, {body: formData})	// headers: {'Content-Type': "multipart/form-data"},
	} else
        Object.assign(options, {headers: {'Content-Type': "application/json"}, body: JSON.stringify(data)})
	const respPromise = await fetch(url, options)
	if (respPromise.status >= 400)  {
		const errorText = await respPromise.text()
		console.error(`COMM-send (${url}): ${respPromise.status}-Error "${errorText}"`, {url, data})
		throw Error(errorText)
	}
	const response = await respPromise.json()      // parses JSON response into native JavaScript objects
    // console.log(`SEND: Data sent as ${method} to ${url} and recieved response: `, response)
	process_updates(response, `${url}-${method.toLowerCase()}`)
	return response
}


export async function send_images(files, params) {
	if (!Array.isArray(files))  files = [files]
	console.log(`COMM-send_images: upload images ${files.map(file => file.name).join(", ")} to server with params `, params);
	const formData = new FormData()
	Object.assign(params, {client: uuid, player: user.ID})
	formData.append('data', JSON.stringify(params))		// server extracts params from JSON in form field 'data'
	files.forEach((file, nr) => formData.append(`img${nr}`,file))
	const respPromise = await fetch('/exhibit/_', {method: "POST", body: formData})
	if (respPromise.status >= 400) {
		const errorText = await respPromise.text()
		console.error(`  – response: ${respPromise.status}-Error "${errorText}"`, {files, params})
		throw Error(errorText)
	}
	const response = await respPromise.json()
	process_updates(response, '/exhibits-post')
	return response
}




/*** socket communication with server ***/

let socket = null       // socketIO will be set as soon as session is established
let updateID = null     // updates have IDs to avoid double updating

/** initialize socketIO with pentelion server **/
function connect_socket() {
	socket = io({ reconnection: true,             // whether to reconnect automatically
                    reconnectionAttempts: Infinity, // number of reconnection attempts before giving up
                    reconnectionDelay: 1000,        // how long to initially wait before attempting a new reconnection
                    reconnectionDelayMax: 5000,     // maximum amount of time to wait between reconnection attempts. Each attempt increases the reconnection delay by 2x along with a randomization factor
                    randomizationFactor: 0.5,
                    path: "/ws/socket.io",
                    // transports: ['websocket', 'polling', 'flashsocket'],
                    maxHttpBufferSize: 4e6})        // 4 MB maximum size for map image uploads
	socket.on('updates', process_updates)
	// console.log(`COMM-socket: Connected via socketio with ident = ${socket.id}`)
}

/** send updates to user and game objects **/
function process_updates(payload, source = "socketio") {
	if (isEmpty(payload))   return
	/// ToDo: check update ID for lost packages
	if ('updateID' in payload)
		delete payload['updateID']
	console.log(`  – updates received from ${source}: `, payload)
	/// distribute the collections to User and Game
	for (const group in payload) {
		if (group === 'game')
			continue        // treat game updates at the end of the update process
		else if (group in user)
			user.update(group, payload[group])
		else if (group in game)
			game.update_collection(group, payload[group])
		else if (group === 'updates') {     // single updates via sockets
			for (const updata of payload.updates) {
				const collect = updata.TYPE.toLowerCase() + "s"
				// const collect = (updata.TYPE.charAt(updata.TYPE.length - 1) === "y") ? updata.TYPE.slice(0, -1).toLowerCase() + "ies" : updata.TYPE.toLowerCase() + "s"
				// console.log(collect)
				if (collect in user)
					user.update(collect, [updata])
				if (collect in game)
					game.update(collect, [updata])
				if (updata.TYPE in game) {
					if (updata.DELETED)
						game[updata.TYPE] = null
					else if (game[updata.TYPE])
						Object.assign(game[updata.TYPE], updata)
					else
						game[updata.TYPE] = updata
				}
			}
		}
	}
	/// process game updates
	if (payload.game && game.ID)
		game.update(payload.game)
	/// process timeline updates
	if (payload.infos)
		view.update_timeline(payload.infos)
	/// process view update
	if (view.map && view.persona && 'personas' in payload) {
		const viewPersUpdate = payload.personas.find(pers => pers.ID === view.persona.ID)
		if (viewPersUpdate?.position && viewPersUpdate.position.map !== view.map.ID)
			view.switch_map(viewPersUpdate.position.map)
	}
}

function update_mapCollection(collection, updData) {
	const objID = updData.ID
	/// update collection
    if (updData.DELETED)  // delete object from collection
        collection.delete(objID)
    else if (collection.has(objID))   // update existing object
        Object.assign(collection.get(objID), updData)
    else {	// create
        const newObject = $state(updData)
        collection.set(objID, newObject)
    }
}


export function send_request(type, data, logmsg = "", callback = null) {
	// add actor to request if not provided (or master requests)
	if (['order', 'alter', 'prepare', 'preview', 'message', 'evaluate'].includes(type) && !('actor' in data) && view.persona)
        data['actor'] = view.persona.ID
	if (type === 'update' && user.isMaster)
        data['isMaster'] = true
	if (!callback)
        callback = (response) => {}
	socket.emit(type, data, callback)
	console.log(`# ${type.toUpperCase()}: ${logmsg} with`, $state.snapshot(data))
}
//ToDo: implement async send_request



/*** handling of errors ***/

export function log_message(message) {
	lastMessage = message
	console
}

// if (location.hostname === 'pentelion.de') {
window.onunhandledrejection = (e) => handle_error(e.reason.message, e.reason.stack.split("\n").slice(0, 3), true)
window.addEventListener('error', (err) => handle_error(err.message, err.stack, true))
window.onpopstate = (e) => view.retrace_layout(e.state)


let lastError
export function handle_error(issue, stack, critical=false) {
	if (lastError === issue)  return
	lastError = issue
	send_data('issue', {client: uuid, issue: issue, stack: stack})
	if (location.hostname === 'pentelion.de') {
        const lastReload = Number(sessionStorage.getItem('reload'))
        const thisReload = Date.now()
        if ((!lastReload || lastReload < thisReload - 2000) && stack && critical &&
            window.confirm(`Ein Fehler im Client ist aufgetreten: ${issue}. Soll die App neu geladen werden (F5)?`)) {
            sessionStorage.setItem('reload', thisReload.toString())
            location.reload()
        }
	}
}

/*** send information on issue to server ***/
export function report_issue(issue) {
	send_data('issue', {client: uuid, issue: issue})
	console.warn(`An issue has been reported to the server: ${issue}`)
}

/** other functions **/

export function sound_feedback(type) {

}


export function select_content() {
	/** selects all content / text in an input **/
	const sel = document.getSelection();
	sel.setBaseAndExtent(this.firstChild, 0, this.lastChild, this.innerText.length)
}

// console.log("CLIENT module has been imported")
